/**
 * 🏪 Main Store - Store principal del sistema
 * Funciones centrales para manejo de estado global
 * MGI - Sistema Unificado - Sprint 2 Día 1
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UserRole, User } from '@/types'
import { useAuthStore } from './auth'
import { useRouter } from 'vue-router'

export const useMainStore = defineStore('main', () => {
  // Estado
  const currentUser = ref<User | null>(null)
  const isLoading = ref(false)
  const notifications = ref<any[]>([])

  // Stores relacionados
  const authStore = useAuthStore()
  const router = useRouter()

  // Computed
  const userRole = computed(() => currentUser.value?.role || 'department_user')
  const user = computed(() => currentUser.value)

  // Métodos de autenticación y permisos
  const canRead = (permission: string) => {
    // Implementación simplificada para demo
    return true
  }

  const canWrite = (permission: string) => {
    // Implementación simplificada para demo
    return ['super_admin', 'group_manager', 'department_head'].includes(userRole.value)
  }

  // Métodos de navegación
  const openModal = (modalName: string, params?: any) => {
    console.log('Opening modal:', modalName, params)
    // Implementar lógica de modales
  }

  // Métodos de notificaciones
  const showNotification = (notification: {
    type: 'success' | 'error' | 'warning' | 'info'
    title: string
    message: string
  }) => {
    const newNotification = {
      id: Date.now(),
      ...notification,
      timestamp: new Date()
    }
    
    notifications.value.push(newNotification)
    
    // Auto-remove después de 5 segundos
    setTimeout(() => {
      notifications.value = notifications.value.filter(n => n.id !== newNotification.id)
    }, 5000)
  }

  // Métodos de datos del dashboard
  const loadDashboardData = async (role: UserRole, props: any) => {
    isLoading.value = true
    
    try {
      // Simular carga de datos
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const mockData = {
        chartData: {
          labels: ['Ene', 'Feb', 'Mar', 'Abr', 'May'],
          datasets: [{
            label: `Datos para ${role}`,
            data: [12, 19, 3, 5, 2],
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)'
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false
        }
      }
      
      return mockData
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  // Métodos de acciones de tabla
  const executeTableAction = (action: string, item: any, role: UserRole) => {
    console.log('Executing table action:', { action, item, role })
    
    switch (action) {
      case 'edit':
        openModal('edit-item', { item })
        break
      case 'delete':
        if (confirm('¿Está seguro de que desea eliminar este elemento?')) {
          showNotification({
            type: 'success',
            title: 'Elemento eliminado',
            message: 'El elemento ha sido eliminado exitosamente'
          })
        }
        break
      case 'validate':
        showNotification({
          type: 'success',
          title: 'Incidencia validada',
          message: 'La incidencia ha sido validada correctamente'
        })
        break
      case 'assign':
        openModal('assign-incident', { incident: item })
        break
      case 'view':
        openModal('view-item', { item })
        break
      default:
        console.log('Unknown action:', action)
    }
  }

  // API simulada
  const api = {
    get: async (url: string, config?: any) => {
      console.log('API GET:', url, config)
      // Simular respuesta API
      return {
        data: {
          labels: ['Ene', 'Feb', 'Mar', 'Abr', 'May'],
          datasets: [{
            label: 'Demo Data',
            data: [12, 19, 3, 5, 2],
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)'
          }]
        }
      }
    },
    post: async (url: string, data: any) => {
      console.log('API POST:', url, data)
      return { data: { success: true } }
    }
  }

  // Inicialización
  const initializeUser = () => {
    // Simular usuario logueado
    currentUser.value = {
      id: 1,
      name: 'Usuario Demo',
      email: 'demo@mgi.com',
      role: 'super_admin' as UserRole,
      agencyId: 1,
      groupId: 1,
      departmentId: 1
    }
  }

  // Método para cambiar usuario actual (necesario para demos)
  const setCurrentUser = (user: User) => {
    currentUser.value = user
  }

    return {
    // Estado
    currentUser,
    isLoading,
    notifications,
    
    // Computed
    userRole,
    user,
    
    // Métodos
    canRead,
    canWrite,
    openModal,
    showNotification,
    loadDashboardData,
    executeTableAction,
    initializeUser,
    setCurrentUser,
    
    // API
    api,
    
    // Router (para compatibilidad)
    router
  }
})
